import { useCallback, useMemo } from 'react';
import { STATUS_OPTIONS } from '../../components/filters/StatusFilter';
import { createColumnFilterRenderer } from '../../components/tables/createColumnFilterRenderer';
import {
  useGetCustomersQuery,
  useGetMachinesQuery,
  useGetPlantsQuery,
} from '../../gql/generated';
import {
  EDGE_DEVICE_CONNECTION_STATUS_OPTIONS,
  EDGE_DEVICE_STATUS_OPTIONS,
  useMachineFilters,
} from '../../hooks/useMachineFilters';
import { Machine } from '../../types/machines/machine.types';
import { toOptions, uniqueOptions } from '../../utils/options';

/**
 * Hook that returns a columnFilters render function for the DataTable.
 * Each column gets its corresponding filter component wrapped in a TableColumnFilter
 * popover that appears when clicking the filter icon in the column header.
 * Reads/writes filter state directly from URL query params - no props needed.
 *
 * Filters are dynamically generated based on machine data - when EDMS adds new
 * properties, the options will automatically appear if the column is enabled.
 */
export function useMachineColumnFilters() {
  const {
    filters,
    setFilter,
    setDisplayName,
    setSerial,
    setPartNumber,
    setCustomers,
    setPlants,
    setFamilies,
    setStatuses,
    setCountryCodes,
    setCountries,
    setCities,
    setStates,
  } = useMachineFilters();

  // Fetch data for filters
  const { data: customersData, loading: customersLoading } =
    useGetCustomersQuery({
      fetchPolicy: 'cache-and-network',
    });

  const { data: plantsData, loading: plantsLoading } = useGetPlantsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { data: machinesData, loading: machinesLoading } = useGetMachinesQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Transform options - these are dynamically generated from the data
  const customerOptions = useMemo(
    () =>
      toOptions(
        customersData?.customers,
        (customer) => customer.id,
        (customer) => customer.name || customer.id
      ),
    [customersData]
  );

  const plantOptions = useMemo(
    () =>
      toOptions(
        plantsData?.plants,
        (plant) => plant.id,
        (plant) => plant.displayName || plant.id
      ),
    [plantsData]
  );

  // Dynamic options derived from machine data - automatically includes new values
  const familyOptions = useMemo(
    () =>
      uniqueOptions(
        machinesData?.machines?.rows
          .map((machine) => machine.family)
          .filter((v): v is string => !!v) || []
      ),
    [machinesData]
  );

  const countryCodeOptions = useMemo(
    () =>
      uniqueOptions(
        machinesData?.machines?.rows
          .map((machine) => machine.address?.countryCode)
          .filter((v): v is string => !!v) || []
      ),
    [machinesData]
  );

  const countryOptions = useMemo(
    () =>
      uniqueOptions(
        machinesData?.machines?.rows
          .map((machine) => machine.address?.country)
          .filter((v): v is string => !!v) || []
      ),
    [machinesData]
  );

  const cityOptions = useMemo(
    () =>
      uniqueOptions(
        machinesData?.machines?.rows
          .map((machine) => machine.address?.city)
          .filter((v): v is string => !!v) || []
      ),
    [machinesData]
  );

  const stateOptions = useMemo(
    () =>
      uniqueOptions(
        machinesData?.machines?.rows
          .map((machine) => machine.address?.state)
          .filter((v): v is string => !!v) || []
      ),
    [machinesData]
  );

  const statusOptions = useMemo(() => STATUS_OPTIONS, []);

  // Change handlers
  const handleDisplayNameChange = useCallback(
    (value: string) => setDisplayName(value),
    [setDisplayName]
  );

  const handleSerialChange = useCallback(
    (value: string) => setSerial(value),
    [setSerial]
  );

  const handlePartNumberChange = useCallback(
    (value: string) => setPartNumber(value),
    [setPartNumber]
  );

  const handleCustomersChange = useCallback(
    (values: string[]) => setCustomers(values),
    [setCustomers]
  );

  const handlePlantsChange = useCallback(
    (values: string[]) => setPlants(values),
    [setPlants]
  );

  const handleFamiliesChange = useCallback(
    (values: string[]) => setFamilies(values),
    [setFamilies]
  );

  const handleStatusesChange = useCallback(
    (values: string[]) => setStatuses(values),
    [setStatuses]
  );

  const handleCountryCodesChange = useCallback(
    (values: string[]) => setCountryCodes(values),
    [setCountryCodes]
  );

  const handleCountriesChange = useCallback(
    (values: string[]) => setCountries(values),
    [setCountries]
  );

  const handleCitiesChange = useCallback(
    (values: string[]) => setCities(values),
    [setCities]
  );

  const handleStatesChange = useCallback(
    (values: string[]) => setStates(values),
    [setStates]
  );

  const handleEdgeDeviceStatusesChange = useCallback(
    (values: string[]) => setFilter('edgeDeviceStatuses', values),
    [setFilter]
  );

  const handleEdgeDeviceConnectionStatusesChange = useCallback(
    (values: string[]) => setFilter('edgeDeviceConnectionStatuses', values),
    [setFilter]
  );

  const handleEdgeDeviceGuidChange = useCallback(
    (value: string) => setFilter('edgeDeviceGuid', value),
    [setFilter]
  );

  const handleEdgeDeviceIdChange = useCallback(
    (value: string) => setFilter('edgeDeviceId', value),
    [setFilter]
  );

  const handleEdgeDeviceNameChange = useCallback(
    (value: string) => setFilter('edgeDeviceName', value),
    [setFilter]
  );

  return createColumnFilterRenderer<Machine>([
    // Core filters (always available)
    {
      columnId: 'displayName',
      kind: 'search',
      value: filters.displayName,
      onChange: handleDisplayNameChange,
      placeholder: 'Search machines...',
    },
    {
      columnId: 'customerName',
      kind: 'select',
      selectedValues: filters.customers,
      onChange: handleCustomersChange,
      options: customerOptions,
      placeholder: 'Search customers...',
      loading: customersLoading,
    },
    {
      columnId: 'plantName',
      kind: 'select',
      selectedValues: filters.plants,
      onChange: handlePlantsChange,
      options: plantOptions,
      placeholder: 'Search plants...',
      loading: plantsLoading,
    },
    {
      columnId: 'family',
      kind: 'select',
      selectedValues: filters.families,
      onChange: handleFamiliesChange,
      options: familyOptions,
      placeholder: 'Search families...',
      loading: machinesLoading,
    },
    {
      columnId: 'status',
      kind: 'select',
      selectedValues: filters.statuses,
      onChange: handleStatusesChange,
      options: statusOptions,
      placeholder: 'Search statuses...',
    },

    // Machine identifier filters
    {
      columnId: 'serial',
      kind: 'search',
      value: filters.serial,
      onChange: handleSerialChange,
      placeholder: 'Search serial...',
    },
    {
      columnId: 'partNumber',
      kind: 'search',
      value: filters.partNumber,
      onChange: handlePartNumberChange,
      placeholder: 'Search part number...',
    },

    // Address/location filters
    {
      columnId: 'countryCode',
      kind: 'select',
      selectedValues: filters.countryCodes,
      onChange: handleCountryCodesChange,
      options: countryCodeOptions,
      placeholder: 'Search country codes...',
      loading: machinesLoading,
    },
    {
      columnId: 'country',
      kind: 'select',
      selectedValues: filters.countries,
      onChange: handleCountriesChange,
      options: countryOptions,
      placeholder: 'Search countries...',
      loading: machinesLoading,
    },
    {
      columnId: 'city',
      kind: 'select',
      selectedValues: filters.cities,
      onChange: handleCitiesChange,
      options: cityOptions,
      placeholder: 'Search cities...',
      loading: machinesLoading,
    },
    {
      columnId: 'state',
      kind: 'select',
      selectedValues: filters.states,
      onChange: handleStatesChange,
      options: stateOptions,
      placeholder: 'Search states...',
      loading: machinesLoading,
    },

    // Edge device filters
    {
      columnId: 'edgeDeviceStatus',
      kind: 'select',
      selectedValues: filters.edgeDeviceStatuses,
      onChange: handleEdgeDeviceStatusesChange,
      options: EDGE_DEVICE_STATUS_OPTIONS,
      placeholder: 'Search provisioning state...',
    },
    {
      columnId: 'edgeDeviceConnectionStatus',
      kind: 'select',
      selectedValues: filters.edgeDeviceConnectionStatuses,
      onChange: handleEdgeDeviceConnectionStatusesChange,
      options: EDGE_DEVICE_CONNECTION_STATUS_OPTIONS,
      placeholder: 'Search connection state...',
    },
    {
      columnId: 'edgeDeviceGuid',
      kind: 'search',
      value: filters.edgeDeviceGuid,
      onChange: handleEdgeDeviceGuidChange,
      placeholder: 'Search edge device ID...',
    },
    {
      columnId: 'edgeDeviceId',
      kind: 'search',
      value: filters.edgeDeviceId,
      onChange: handleEdgeDeviceIdChange,
      placeholder: 'Search edge device DB ID...',
    },
    {
      columnId: 'edgeDeviceName',
      kind: 'search',
      value: filters.edgeDeviceName,
      onChange: handleEdgeDeviceNameChange,
      placeholder: 'Search edge device name...',
    },
  ]);
}
